<template>
  <portal-layout>
    <div class="problem-page">
      <div class="page-head">
        <h2 class="page-title">题目列表</h2>
        <div class="search-bar">
          <el-input v-model="keyword" placeholder="搜索题目编号或标题" clearable size="small"
                    class="search-input" @keyup.enter.native="handleSearch" @clear="handleSearch">
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
          <el-select v-model="difficulty" placeholder="难度" clearable size="small"
                     class="difficulty-select" @change="handleSearch">
            <el-option label="简单" :value="0"></el-option>
            <el-option label="中等" :value="1"></el-option>
            <el-option label="困难" :value="2"></el-option>
          </el-select>
        </div>
      </div>

      <div v-loading="loading" class="problem-table">
        <el-table :data="problems" stripe style="width: 100%">
          <el-table-column prop="problemId" label="题号" width="150"></el-table-column>
          <el-table-column label="标题" min-width="240">
            <template slot-scope="scope">
              <span class="problem-title-link" @click="goDetail(scope.row)">{{ scope.row.title }}</span>
            </template>
          </el-table-column>
          <el-table-column label="难度" width="100">
            <template slot-scope="scope">
              <el-tag :type="difficultyTagType(scope.row.difficulty)" size="mini">
                {{ difficultyText(scope.row.difficulty) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="时间限制" width="110">
            <template slot-scope="scope">{{ scope.row.timeLimit }} ms</template>
          </el-table-column>
          <el-table-column label="内存限制" width="110">
            <template slot-scope="scope">{{ scope.row.memoryLimit }} MB</template>
          </el-table-column>
          <el-table-column label="我的状态" width="110">
            <template slot-scope="scope">
              <span v-if="scope.row.myStatus === null || scope.row.myStatus === undefined"
                    class="status-none">未提交</span>
              <span v-else :class="['status-text', statusClass(scope.row.myStatus)]">
                {{ statusText(scope.row.myStatus) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="goDetail(scope.row)">去做题</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div v-if="!loading && problems.length === 0" class="empty-tip">暂无题目</div>
      </div>

      <div v-if="total > 0" class="pagination-container">
        <el-pagination background layout="total, prev, pager, next" :total="total"
                       :current-page.sync="pageNum" :page-size="pageSize"
                       @current-change="queryProblems"></el-pagination>
      </div>
    </div>
  </portal-layout>
</template>
<script>
import PortalLayout from '@/components/PortalLayout.vue';
import api from '@/common/api';
import storage from '@/common/storage.js';

// 判题状态短名与样式映射，与后端 JudgeConstants.JudgeStatus 对应
const STATUS_TEXT = {
  '-10': '未提交', '-5': '未知', '-4': '已取消', '-3': '格式错误', '-2': '编译错误',
  '-1': '答案错误', '0': '通过', '1': '超时', '2': '超内存', '3': '运行错误',
  '4': '系统错误', '5': '等待中', '6': '编译中', '7': '判题中', '8': '部分通过', '9': '提交中', '10': '提交失败'
};

export default {
  name: 'PortalProblemList',
  components: { PortalLayout },
  data() {
    return {
      loading: false,
      keyword: '',
      difficulty: null,
      problems: [],
      total: 0,
      pageNum: 1,
      pageSize: 10
    }
  },
  created() {
    if (!this.checkLogin()) {
      return;
    }
    this.queryProblems();
  },
  methods: {
    checkLogin() {
      if (!storage.getString('portalMember')) {
        if (this.$route.name === 'PortalLogin') return false;
        this.$router.push({ name: 'PortalLogin', query: { redirect: this.$route.fullPath } }).catch(() => {});
        return false;
      }
      return true;
    },
    queryProblems() {
      this.loading = true;
      api.hoj_problem_list({
        keyword: this.keyword,
        difficulty: this.difficulty,
        pageNum: this.pageNum,
        pageSize: this.pageSize
      }).then((r) => {
        if (r.data.code === "200") {
          // 后端 R.ok(page) 走 data 字段，返回 MyBatis-Plus 分页对象
          const page = r.data.data || {};
          this.problems = page.records || [];
          this.total = page.total || 0;
        }
      }).catch(() => {
        // 401 已由 common/api.js 统一跳转登录页
      }).finally(() => {
        this.loading = false;
      });
    },
    handleSearch() {
      this.pageNum = 1;
      this.queryProblems();
    },
    goDetail(row) {
      this.$router.push('/portal/problem/detail?id=' + row.id).catch(() => {});
    },
    difficultyText(difficulty) {
      const map = { 0: '简单', 1: '中等', 2: '困难' };
      return map[difficulty] || '未知';
    },
    difficultyTagType(difficulty) {
      const map = { 0: 'success', 1: 'warning', 2: 'danger' };
      return map[difficulty] || 'info';
    },
    statusText(status) {
      return STATUS_TEXT[String(status)] || '未知';
    },
    statusClass(status) {
      const s = String(status);
      if (s === '0') return 'status-ac';
      if (s === '-1' || s === '-2' || s === '-3' || s === '1' || s === '2' || s === '3' || s === '4') return 'status-error';
      return 'status-other';
    }
  }
};
</script>
<style scoped>
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-title {
  font-size: 22px;
  color: #303133;
  margin: 0;
}
.search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
}
.search-input {
  width: 280px;
}
.difficulty-select {
  width: 110px;
}
.problem-table {
  background-color: #fff;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: 200px;
}
.problem-title-link {
  color: #303133;
  cursor: pointer;
}
.problem-title-link:hover {
  color: #409EFF;
}
.status-none {
  color: #909399;
  font-size: 13px;
}
.status-text {
  font-size: 13px;
  font-weight: 500;
}
.status-ac {
  color: #67C23A;
}
.status-error {
  color: #F56C6C;
}
.status-other {
  color: #E6A23C;
}
.empty-tip {
  text-align: center;
  color: #909399;
  padding: 50px 0;
  font-size: 14px;
}
.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style>
